import { useNavigate } from "react-router-dom";
import { useLiveData } from "@/lib/useLiveData";
import { PageHeader, Loader, KpiCard } from "@/crm/ui";
import LiveBadge from "@/crm/components/LiveBadge";
import CallStats from "@/crm/components/CallStats";
import { Users, Phone, CalendarClock, Trophy, Target, TrendingUp } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function Reports() {
  const navigate = useNavigate();
  const { data, refreshing, lastUpdated, refresh } = useLiveData("/dashboard/manager", { interval: 15000 });
  if (!data) return <Loader />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="Rapports & performance" subtitle="Vue d'ensemble de l'équipe commerciale"
        actions={<div className="flex items-center gap-3">
          <LiveBadge refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
          <button data-testid="compare-btn" onClick={() => navigate("/comparaison")} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"><Trophy size={16} /> Comparer les commerciaux</button>
        </div>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Total prospects" value={k.total_prospects} icon={Users} accent="indigo" />
        <KpiCard label="Contactés" value={k.contactes} icon={Phone} accent="sky" />
        <KpiCard label="Taux de contact" value={`${k.taux_contact}%`} icon={Target} accent="violet" />
        <KpiCard label="Non attribués" value={k.non_attribues} icon={Users} accent="amber" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Rendez-vous" value={k.rdv} icon={CalendarClock} accent="indigo" />
        <KpiCard label="Ventes gagnées" value={k.gagnes} icon={Trophy} accent="emerald" />
        <KpiCard label="Taux de conversion" value={`${k.taux_conversion}%`} icon={TrendingUp} accent="emerald" />
        <div />
      </div>

      <CallStats />

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <h3 className="font-heading font-semibold text-slate-800 mb-4">Performance par commercial</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.team}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip /><Legend />
            <Bar dataKey="prospects" name="Prospects" fill="#4f46e5" radius={[4, 4, 0, 0]} />
            <Bar dataKey="appels" name="Appels" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            <Bar dataKey="rdv" name="RDV" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="gagnes" name="Gagnés" fill="#059669" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase">
            <tr><th className="p-3 text-left">Commercial</th><th className="p-3 text-right">Prospects</th><th className="p-3 text-right">Appels</th><th className="p-3 text-right">RDV</th><th className="p-3 text-right">Gagnés</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.team.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`team-row-${r.id}`}>
                <td className="p-3 font-medium text-slate-800">{r.name}</td>
                <td className="p-3 text-right font-mono">{r.prospects}</td>
                <td className="p-3 text-right font-mono">{r.appels}</td>
                <td className="p-3 text-right font-mono">{r.rdv}</td>
                <td className="p-3 text-right font-mono text-emerald-600 font-semibold">{r.gagnes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
